// Deployed as cobei-it/client/src/utils/operateSso.js.
import axios from 'axios';
const api = axios.create({ baseURL: process.env.VUE_APP_API_BASE || '/', withCredentials: true, timeout: 15000 });
const parentOrigin = process.env.VUE_APP_OPERATE_ORIGIN || window.location.origin;
const tokenKey = 'cobei-sso-token';
let active;
let router;
const random = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
const localPath = path => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');
export const isEmbedded = () => window.parent !== window;
export const legacyToken = () => isEmbedded() ? sessionStorage.getItem(tokenKey) : localStorage.getItem('access_token');
export function localLoginTarget(target) {
  return localPath(target) && !/^\/(login|sso)(\/|\?|#|$)/.test(target) ? target : '/dashboard/main';
}
export function clearAuth() {
  if (isEmbedded()) sessionStorage.removeItem(tokenKey);
  else localStorage.removeItem('access_token');
}
async function exchange(ticket, verifier) {
  const { data } = await api.post('/vue/sso/exchange', { ticket, verifier });
  if (data.code !== 1) throw new Error(data.message || '统一登录失败');
  sessionStorage.setItem(tokenKey, data.access_token);
  return data.target;
}
async function authenticate(target) {
  const verifier = random();
  const challenge = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))), b => b.toString(16).padStart(2, '0')).join('');
  if (isEmbedded()) {
    return new Promise((resolve, reject) => {
      const cleanup = () => { clearTimeout(timer); window.removeEventListener('message', receive); };
      const receive = async event => {
        if (event.origin !== parentOrigin || event.source !== window.parent || event.data?.challenge !== challenge) return;
        if (event.data.type === 'operate:sso-error') { cleanup(); reject(new Error(event.data.message)); }
        if (event.data.type !== 'operate:sso-ticket') return;
        cleanup();
        try { await exchange(event.data.ticket, verifier); resolve(); } catch (e) { reject(e); }
      };
      const timer = setTimeout(() => { cleanup(); reject(new Error('统一登录超时，请在主页面重新加载')); }, 25000);
      window.addEventListener('message', receive);
      window.parent.postMessage({ type: 'cobei:sso-ready', challenge }, parentOrigin);
    });
  }
  const state = random();
  sessionStorage.setItem('cobei-sso-request', JSON.stringify({ verifier, state, time: Date.now() }));
  const params = new URLSearchParams({ target, challenge, state });
  window.location.replace(`${parentOrigin}/new/#/sso/authorize?${params}`);
  return new Promise(() => {});
}
export function ensureAuth(target) {
  if (!active) active = authenticate(target).finally(() => { active = undefined; });
  return active;
}
export function installSso(instance) {
  router = instance;
  router.beforeEach(async (to, from, next) => {
    if (to.path === '/sso/error') { next(); return; }
    if (to.path === '/sso/callback') {
      const params = new URLSearchParams(window.location.hash.slice(1));
      history.replaceState(null, '', '/sso/callback');
      try {
        const pending = JSON.parse(sessionStorage.getItem('cobei-sso-request') || 'null');
        sessionStorage.removeItem('cobei-sso-request');
        if (!pending || pending.state !== params.get('state') || Date.now() - pending.time > 600000) throw new Error('登录回调已过期');
        const target = await exchange(params.get('ticket'), pending.verifier);
        if (!localPath(target)) throw new Error('返回地址无效');
        next({ path: target, replace: true });
      } catch (e) { next({ path: '/sso/error', query: { message: e.response?.data?.message || e.message }, replace: true }); }
      return;
    }
    if (!isEmbedded()) {
      // Opening the standalone root/login must show this project's login page,
      // even if the same browser previously signed in through operate.
      if (to.path === '/login') { next(); return; }
      try {
        const token = legacyToken();
        if (token) {
          const { data } = await api.get('/vue/check_session', {
            params: { _t: Date.now() }, headers: { Authorization: `Bearer ${token}` },
          });
          if (data.code === 1) { next(); return; }
        }
      } catch (e) { /* Standalone failures return to the local login, never SSO. */ }
      clearAuth();
      next({ path: '/login', query: { redirect: to.fullPath }, replace: true });
      return;
    }
    const target = to.path === '/login' ? '/dashboard/auto_update' : to.fullPath;
    try {
      const token = legacyToken();
      if (token) {
        const { data } = await api.get('/vue/check_session', { headers: { Authorization: `Bearer ${token}` } });
        if (data.code === 1) { next(to.path === '/login' ? target : undefined); return; }
      }
    } catch (e) {
      if (e.response?.status !== 401) { next({ path: '/sso/error', query: { message: '登录校验服务暂不可用' } }); return; }
    }
    clearAuth();
    try { await ensureAuth(target); next(to.path === '/login' ? target : undefined); }
    catch (e) { next({ path: '/sso/error', query: { message: e.response?.data?.message || e.message }, replace: true }); }
  });
  window.addEventListener('storage', event => {
    if (!isEmbedded()) return;
    if (event.key !== 'operate-auth-change') return;
    clearAuth();
    window.location.replace('/sso/error?message=' + encodeURIComponent('账号状态已变化，请重新进入页面'));
  });
}
export function handleUnauthorized() {
  clearAuth();
  if (!router || router.currentRoute.path.startsWith('/sso/')) return;
  const target = router.currentRoute.fullPath;
  if (!isEmbedded()) {
    if (router.currentRoute.path !== '/login') router.replace({ path: '/login', query: { redirect: target } });
    return;
  }
  // Restore authentication only. Never replay a mutation automatically.
  ensureAuth(target).then(() => {
    router.app.$message?.warning('登录状态已恢复，请重新执行刚才的操作');
  }).catch(() => router.replace({ path: '/sso/error', query: { message: '登录状态已失效，请重新进入' } }));
}
